import { Link } from "react-router";

type Variant = "primary" | "outline" | "ghost";
type Size = "sm" | "md" | "lg";

const base =
  "inline-flex items-center justify-center gap-2 rounded-lg font-heading font-semibold uppercase tracking-wider transition disabled:cursor-not-allowed disabled:opacity-50";

const variants: Record<Variant, string> = {
  // Dark text on orange — reads in light + dark. `brightness` darkens the
  // orange on hover (an opacity fade would wash lighter on a light page).
  primary: "bg-accent text-ink hover:brightness-95",
  outline:
    "border border-border-shell bg-transparent text-text-full hover:border-accent hover:text-accent-text dark:hover:text-accent",
  ghost: "text-text-medium hover:bg-surface-elevated hover:text-text-full",
};

const sizes: Record<Size, string> = {
  sm: "px-4 py-1.5 text-xs",
  md: "px-6 py-2.5 text-sm",
  lg: "px-8 py-3 text-sm",
};

export const buttonClasses = (
  variant: Variant = "primary",
  size: Size = "md",
  className = "",
) => `${base} ${variants[variant]} ${sizes[size]} ${className}`;

type StyleProps = {
  variant?: Variant;
  size?: Size;
  className?: string;
  children: React.ReactNode;
};

export const Button = ({
  variant,
  size,
  className,
  children,
  ...rest
}: StyleProps & React.ButtonHTMLAttributes<HTMLButtonElement>) => (
  <button
    type="button"
    className={buttonClasses(variant, size, className)}
    {...rest}
  >
    {children}
  </button>
);

export const ButtonLink = ({
  to,
  variant,
  size,
  className,
  children,
}: StyleProps & { to: string }) => (
  <Link to={to} className={buttonClasses(variant, size, className)}>
    {children}
  </Link>
);

export const ButtonAnchor = ({
  variant,
  size,
  className,
  children,
  ...rest
}: StyleProps & React.AnchorHTMLAttributes<HTMLAnchorElement>) => (
  <a className={buttonClasses(variant, size, className)} {...rest}>
    {children}
  </a>
);
